import * as THREE from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";

import {
    describeAndroidHostedModelSource,
    describeIosHostedModelSource,
    resolveAndroidHostedModelUrl,
    resolveIosHostedModelUrl,
} from "./arHosting.service";

const getUserAgent = () => {
    if (typeof window === "undefined") return "";
    return window.navigator.userAgent || "";
};

export const getARPlatform = () => {
    const userAgent = getUserAgent();

    if (/android/i.test(userAgent)) {
        return "android";
    }

    if (/iPad|iPhone|iPod/i.test(userAgent)) {
        return "ios";
    }

    return "desktop";
};

export const getHostedARModelUrls = (options = {}) => ({
    androidModelUrl:
        resolveAndroidHostedModelUrl(options) ||
        process.env.REACT_APP_AR_MODEL_URL ||
        "",
    androidModelSource: describeAndroidHostedModelSource(options),
    iosModelUrl: resolveIosHostedModelUrl(options),
    iosModelSource: describeIosHostedModelSource(options),
});

const cloneMaterial = (material) => {
    if (!material) return material;
    if (Array.isArray(material)) {
        return material.map((item) => (item?.clone ? item.clone() : item));
    }

    return material.clone ? material.clone() : material;
};

const buildExportScene = (scene) => {
    const sourceGroup = scene?.getObjectByName("fullBuilding");

    if (!sourceGroup) {
        throw new Error("The building scene is not ready yet.");
    }

    const exportScene = new THREE.Scene();
    const clone = sourceGroup.clone(true);

    clone.traverse((child) => {
        if (child.isMesh) {
            child.material = cloneMaterial(child.material);
            child.frustumCulled = false;
            child.castShadow = false;
            child.receiveShadow = false;
        }
    });

    exportScene.add(clone);

    const ground = scene.getObjectByName("ground");
    if (ground?.clone) {
        const groundClone = ground.clone(true);
        groundClone.traverse((child) => {
            if (child.isMesh) {
                child.material = cloneMaterial(child.material);
                child.frustumCulled = false;
            }
        });
        exportScene.add(groundClone);
    }

    return exportScene;
};

/**
 * Convert any material to MeshStandardMaterial for USDZExporter.
 *
 * Key rules for reliable USDZ output:
 * - USDZExporter ONLY processes MeshStandardMaterial; all others are skipped
 *   (producing invisible/transparent meshes in Quick Look).
 * - Texture maps (panel/siding patterns) reference external image URLs that
 *   USDZExporter cannot embed — including them causes transparency.
 *   We use only the solid hex color from the material instead.
 * - Force fully opaque so ARKit has solid geometry to anchor to a surface.
 */
const toStandardMaterial = (mat) => {
    if (!mat) return new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.7, metalness: 0.1 });

    // Determine base color — works for Phong, Lambert, Basic, Standard, Toon
    const color = mat.color ? mat.color.clone() : new THREE.Color(0xaaaaaa);

    // If the base color is pure white (0xffffff) and there's a texture map,
    // the actual appearance comes from the texture (we can't embed it in USDZ).
    // Fall back to a neutral light grey so it doesn't appear transparent.
    const isWhite = color.r > 0.98 && color.g > 0.98 && color.b > 0.98;
    if (isWhite && mat.map) {
        color.setHex(0xdddddd);
    }

    return new THREE.MeshStandardMaterial({
        color,
        roughness: 0.65,
        metalness: 0.15,
        // Force fully opaque — transparent geometry prevents ARKit surface anchoring
        transparent: false,
        opacity: 1,
        // Double-sided for thin wall panels
        side: mat.side ?? THREE.FrontSide,
        // NO map / normalMap / bumpMap — external texture URLs can't be
        // embedded by USDZExporter and silently make the mesh transparent.
    });
};

const convertMaterialsForUsdz = (material) => {
    if (!material) return new THREE.MeshStandardMaterial({ color: 0xcccccc });
    if (Array.isArray(material)) return material.map(toStandardMaterial);
    return toStandardMaterial(material);
};

// Three.js scene uses feet as its unit (1 unit = 1 foot).
// USDZ / ARKit expects meters (1 unit = 1 metre).
// 1:30 scale: a 30ft building → ~30cm model. Visible on the floor without
// filling the screen. Adjust this constant to change the model size.
const FEET_TO_METERS = 0.3048 / 30; // 1:30 model scale (~1cm per foot)

/**
 * Build an export scene where EVERY material is MeshStandardMaterial,
 * geometry is correctly scaled to real-world metres, and the building
 * is centred on the AR floor plane so it stands upright facing the user.
 *
 * IMPORTANT: We bake the scale and centering transform DIRECTLY into each
 * mesh's geometry (applyMatrix4 on BufferGeometry). This is the only
 * reliable approach — relying on Group.scale + updateMatrixWorld() is
 * fragile because USDZExporter reads matrixWorld per-mesh using the mesh's
 * own accumulated world matrix, and group-level scale can be silently
 * ignored or misread depending on the Three.js / exporter version.
 */
const buildUsdzExportScene = (scene) => {
    const sourceGroup = scene?.getObjectByName("fullBuilding");

    if (!sourceGroup) {
        throw new Error("The building scene is not ready yet.");
    }

    // ── Step 1: clone and convert materials ──────────────────────────────
    const clone = sourceGroup.clone(true);
    clone.traverse((child) => {
        if (child.isMesh) {
            child.material = convertMaterialsForUsdz(child.material);
            child.frustumCulled = false;
            child.castShadow = false;
            child.receiveShadow = false;
        }
    });

    // ── Step 2: compute bounding box of raw geometry ──────────────────────
    // Reset any scene-level position/rotation on the clone so the bounding
    // box reflects geometry only (not inherited scene offsets).
    clone.position.set(0, 0, 0);
    clone.rotation.set(0, 0, 0);
    clone.scale.set(1, 1, 1);
    clone.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(clone);
    const centre = new THREE.Vector3();
    box.getCenter(centre);

    // ── Step 3: build combined transform matrix ───────────────────────────
    // Order: first TRANSLATE so geometric centre → origin (base flush with Y=0)
    //        then SCALE from feet → metres
    // Matrix applied to a vertex v:  v' = S * T * v
    const translateMat = new THREE.Matrix4().makeTranslation(
        -centre.x,
        -box.min.y,   // base sits on the AR floor plane (Y = 0)
        -centre.z
    );
    const scaleMat = new THREE.Matrix4().makeScale(
        FEET_TO_METERS, FEET_TO_METERS, FEET_TO_METERS
    );
    const combinedMat = new THREE.Matrix4().multiplyMatrices(scaleMat, translateMat);

    // ── Step 4: bake the matrix into every mesh's BufferGeometry ─────────
    // applyMatrix4 multiplies every vertex position directly, so the exported
    // vertex data is already in the correct scale/position regardless of how
    // the exporter handles object transforms.
    clone.updateMatrixWorld(true);
    clone.traverse((child) => {
        if (child.isMesh && child.geometry) {
            // Include the mesh's own local-to-clone transform so nested parts
            // (walls, roof, doors, etc.) are positioned correctly.
            const meshWorldMat = child.matrixWorld.clone(); // relative to clone (no parent yet)
            const finalMat = new THREE.Matrix4().multiplyMatrices(combinedMat, meshWorldMat);

            const baked = child.geometry.clone();
            baked.applyMatrix4(finalMat);
            child.geometry = baked;

            // Reset the mesh's own transform — geometry is now in world/export space.
            child.position.set(0, 0, 0);
            child.rotation.set(0, 0, 0);
            child.scale.set(1, 1, 1);
        }
    });

    // ── Step 5: add to export scene with identity transforms ─────────────
    // All transforms are baked into the geometry, so root and clone stay at
    // identity. The USDZ exporter will see correct vertex positions directly.
    clone.position.set(0, 0, 0);
    clone.rotation.set(0, 0, 0);
    clone.scale.set(1, 1, 1);

    const exportScene = new THREE.Scene();
    exportScene.add(clone);
    exportScene.updateMatrixWorld(true);

    return exportScene;
};

export const exportBuildingSceneToGlb = (scene, fileName = "sensei-building.glb") =>
    new Promise((resolve, reject) => {
        try {
            const exportScene = buildExportScene(scene);
            const exporter = new GLTFExporter();

            exporter.parse(
                exportScene,
                (result) => {
                    if (!(result instanceof ArrayBuffer)) {
                        reject(new Error("The AR export did not return a GLB file."));
                        return;
                    }

                    const file = new File([result], fileName, {
                        type: "model/gltf-binary",
                    });

                    resolve(file);
                },
                (error) => reject(error),
                {
                    binary: true,
                    onlyVisible: true,
                    trs: false,
                }
            );
        } catch (error) {
            reject(error);
        }
    });

/**
 * Export the current Three.js building scene as a USDZ file.
 * USDZ is the format required by iOS Quick Look for AR placement.
 * Returns a { file, blobUrl } object — blobUrl can be set directly as
 * the href of an <a rel="ar"> element so the user can tap it natively.
 */
export const exportBuildingSceneToUsdz = async (scene, fileName = "sensei-building.usdz") => {
    // Use the USDZ-specific scene builder that converts ALL materials to
    // MeshStandardMaterial. Other material types are silently skipped by
    // USDZExporter, producing a transparent/invisible model in Quick Look.
    const exportScene = buildUsdzExportScene(scene);
    const exporter = new USDZExporter();

    // USDZExporter.parse() is async and returns an ArrayBuffer (Uint8Array)
    const arraybuffer = await exporter.parse(exportScene, {
        quickLookCompatible: true,
        ar: {
            anchoring: { type: "plane" },
            planeAnchoring: { alignment: "horizontal" },
        },
    });

    const file = new File([arraybuffer], fileName, { type: "model/vnd.usdz+zip" });
    const blobUrl = URL.createObjectURL(file);

    return { file, blobUrl };
};

export const downloadArModel = (file) => {
    const objectUrl = URL.createObjectURL(file);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};

export const canShareArModelFile = (file) => {
    if (!file || !navigator?.canShare) return false;

    try {
        return navigator.canShare({ files: [file] });
    } catch (error) {
        return false;
    }
};

export const shareArModelFile = async (file) => {
    if (!canShareArModelFile(file)) {
        return false;
    }

    await navigator.share({
        title: file.name,
        text: "Open this building model in a compatible AR viewer.",
        files: [file],
    });

    return true;
};

const buildSceneViewerIntentUrl = (modelUrl, title) => {
    const encodedModelUrl = encodeURIComponent(modelUrl);
    const encodedTitle = encodeURIComponent(title);
    const fallbackUrl = encodeURIComponent(modelUrl);

    return `intent://arvr.google.com/scene-viewer/1.0?file=${encodedModelUrl}&mode=ar_preferred&title=${encodedTitle}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${fallbackUrl};end;`;
};

export const looksLikeUsdzUrl = (modelUrl = "") =>
    /\.usdz(\?|#|$)/i.test(String(modelUrl || "").trim());

/**
 * Android only — opens Google Scene Viewer via intent URL.
 * iOS Quick Look must NOT be launched via JS. Instead, render an
 * <a rel="ar"><img></a> element directly in JSX and let the user tap it.
 */
export const openAndroidSceneViewer = (androidModelUrl, title = "Sensei Building") => {
    if (!androidModelUrl) return { ok: false, reason: "No Android GLB URL configured." };
    window.location.href = buildSceneViewerIntentUrl(androidModelUrl, title);
    return { ok: true, reason: "" };
};
